import { AlertCircle, Info, CheckCircle, X, Bell, AlertTriangle, CheckCheck, Inbox } from 'lucide-react';
import { useAppStore } from '../../stores/appStore';

type NotificationType = 'warning' | 'info' | 'success' | 'error';

interface Notification {
  id: string;
  type: NotificationType;
  message: string;
  timestamp: string;
  checklistId?: string;
  isRead?: boolean;
  action?: string;
}

interface NotificationsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onNotificationClick: (checklistId?: string, notificationId?: string, action?: string) => void;
  onMarkAllRead?: () => void;
}

const TYPE_CONFIG = {
  warning: {
    icon: AlertCircle,
    iconColor: 'text-amber-500',
    iconBg: 'bg-amber-50',
    border: 'border-l-amber-400',
    dot: 'bg-amber-400',
  },
  info: {
    icon: Info,
    iconColor: 'text-blue-500',
    iconBg: 'bg-blue-50',
    border: 'border-l-blue-400',
    dot: 'bg-blue-400',
  },
  success: {
    icon: CheckCircle,
    iconColor: 'text-emerald-500',
    iconBg: 'bg-emerald-50',
    border: 'border-l-emerald-400',
    dot: 'bg-emerald-400',
  },
  error: {
    icon: AlertTriangle,
    iconColor: 'text-red-500',
    iconBg: 'bg-red-50',
    border: 'border-l-red-400',
    dot: 'bg-red-400',
  },
};

const ACTION_LABELS: Record<string, string> = {
  validation_results: 'Validation',
  checklist:          'Checklist',
  upload:             'Upload',
  audit:              'Audit',
};

function getRelativeTime(timestamp: string): string {
  if (!timestamp) return 'Just now';
  const normalized = timestamp.endsWith('Z') || timestamp.includes('+') ? timestamp : timestamp + 'Z';
  const date = new Date(normalized);
  if (isNaN(date.getTime())) return 'Just now';
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function NotificationsPanel({
  isOpen,
  onClose,
  onNotificationClick,
  onMarkAllRead,
}: NotificationsPanelProps) {
  const rawNotifications = useAppStore((s) => s.notifications);

  if (!isOpen) return null;

  const notifications: Notification[] = rawNotifications.map((n) => ({
    id: n.id,
    type: (n.type as NotificationType) ?? 'info',
    message: n.message ?? '',
    timestamp: n.timestamp ?? '',
    checklistId: typeof n['submission_id'] === 'string' ? (n['submission_id'] as string) : undefined,
    isRead: n.is_read,
    action: n.action,
  }));

  const unreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />

      {/* Panel */}
      <div className="fixed top-16 right-4 w-[380px] bg-white border border-border rounded-xl shadow-2xl z-50 max-h-[580px] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-white">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Bell className="w-4 h-4 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-foreground">Notifications</h3>
                {unreadCount > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white bg-primary">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </div>
              <p className="text-[10px] text-muted-foreground">
                {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && onMarkAllRead && (
              <button
                onClick={onMarkAllRead}
                className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-primary hover:bg-primary/8 rounded-lg transition-colors"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                Mark all read
              </button>
            )}
            <button
              onClick={onClose}
              aria-label="Close notifications"
              className="p-1.5 hover:bg-secondary rounded-lg transition-colors ml-1"
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-14 px-6 text-center">
              <div className="w-14 h-14 rounded-2xl bg-muted/50 flex items-center justify-center mb-3">
                <Inbox className="w-7 h-7 text-muted-foreground/50" />
              </div>
              <p className="text-sm font-medium text-foreground mb-1">All caught up!</p>
              <p className="text-xs text-muted-foreground">No notifications to show right now.</p>
            </div>
          ) : (
            <div className="divide-y divide-border/60">
              {notifications.map((notification) => {
                const config = TYPE_CONFIG[notification.type] ?? TYPE_CONFIG.info;
                const Icon = config.icon;
                const actionLabel = notification.action ? ACTION_LABELS[notification.action] : null;

                return (
                  <button
                    key={notification.id}
                    onClick={() => onNotificationClick(notification.checklistId, notification.id, notification.action)}
                    className={`w-full px-4 py-3 hover:bg-slate-50 transition-colors text-left border-l-2 ${config.border} ${
                      !notification.isRead ? 'bg-blue-50/40' : 'bg-white'
                    }`}
                  >
                    <div className="flex gap-3">
                      {/* Icon */}
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${config.iconBg}`}>
                        <Icon className={`w-4 h-4 ${config.iconColor}`} />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <p className={`text-xs leading-snug line-clamp-2 ${!notification.isRead ? 'font-medium text-foreground' : 'text-foreground/80'}`}>
                            {notification.message}
                          </p>
                          {!notification.isRead && (
                            <span className={`w-2 h-2 rounded-full flex-shrink-0 mt-1 ${config.dot}`} />
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-muted-foreground">
                            {getRelativeTime(notification.timestamp)}
                          </span>
                          {actionLabel && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[9px] font-semibold uppercase tracking-wide bg-muted text-muted-foreground">
                              {actionLabel}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="px-4 py-2.5 border-t border-border bg-muted/30 text-center">
            <p className="text-[10px] text-muted-foreground">
              Showing {notifications.length} notification{notifications.length !== 1 ? 's' : ''}
            </p>
          </div>
        )}
      </div>
    </>
  );
}
